/**
 * Predicates over schema shapes used in multiple modules. Keep logic here
 * so build.ts and walker.ts can't drift.
 */

import type { JsonSchema } from '@bliksys/shared';

export function isConstraintUnion(variants: readonly JsonSchema[]): boolean {
  if (variants.length === 0) return false;
  return variants.every((v) => {
    if (v.type !== undefined) return false;
    if (v.properties !== undefined) return false;
    if (v.items !== undefined) return false;
    if (v.const !== undefined) return false;
    if (v.enum !== undefined) return false;
    if (v.$ref !== undefined) return false;
    if (v.oneOf !== undefined || v.anyOf !== undefined) return false;
    return true;
  });
}

/**
 * For a oneOf variant schema, return the string value it pins a named
 * property to (via `const` or single-element `enum`), if any.
 */
export function readConstProperty(schema: JsonSchema, propertyName: string): string | undefined {
  const prop = schema.properties?.[propertyName];
  if (!prop) return undefined;
  if (typeof prop.const === 'string') return prop.const;
  if (prop.enum?.length === 1 && typeof prop.enum[0] === 'string') return prop.enum[0];
  return undefined;
}

/**
 * Infer the discriminator property when the schema author didn't declare
 * one. A property is a valid inferred discriminator if every variant pins
 * that property to a constant. First-match-wins on tag collisions (see
 * fstab's diskDevice/diskUUID which both use `type: "disk"`).
 */
export function inferDiscriminator(resolvedVariants: readonly JsonSchema[]): string | undefined {
  if (resolvedVariants.length < 2) return undefined;
  const firstKeys = Object.keys(resolvedVariants[0]!.properties ?? {});
  const candidates = firstKeys.filter((key) =>
    resolvedVariants.every((v) => v.properties?.[key] !== undefined),
  );
  for (const key of candidates) {
    if (resolvedVariants.every((v) => readConstProperty(v, key) !== undefined)) {
      return key;
    }
  }
  return undefined;
}
